/**
 * apply-commands system — v0M3.1.
 *
 * First system in the tick per MECHANICS.md §1.1. Turns validated
 * player intent (via the command stream) into state mutations.
 *
 * v0M3.1 handles `move_army`:
 *   1. Ownership check (faction must own the Army).
 *   2. In-bounds check on target cell.
 *   3. Pathfinder call (`hierarchicalPath` — v0M2.7 spike graduated).
 *   4. On success: stamp `movementGoal` + `plannedPath` onto the Army.
 *   5. On failure: log-and-drop (rejected orders don't crash the tick).
 *
 * Legality per MECHANICS.md §1.3: commands must not depend on
 * information the issuing player cannot see. The pathfinder is
 * fully-deterministic and reads only terrain (visible to all clients);
 * v0M3.1 doesn't gate on visibility. When visibility lands (v0M3.x),
 * out-of-vision target rejection is added here.
 */

import type { Command } from './commands';
import type { GameState } from './state';
import type { RngState } from './rng';
import type { SimContext } from './sim-context';
import { hierarchicalPath, terrainCost } from './pathfind';
import { computeBridgeCells } from './build';
import { applyFormation, cascadeCommanderOrder, createFortAssaultBattleFromArmies, createSiegeBattleFromArmies } from './battle-factory';
// v0M13.1 (2026-07-30) — entry gate. Assaults with a zero-eligible
// attacker are silently rejected (matches other command-side "break //
// Rejected" gates). The auto-triggered siege paths in zoc-detection.ts +
// pillages.ts destroy the attacker army instead (mirrors MFix1.5).
import { armyHasEligibleCohorts } from './battle-entry-gate';
import { loadStructures } from '../data/structures';
import { loadCohorts } from '../data/cohorts';
import { loadKnowledge } from '../data/knowledge';
import { maxArmyBonus, buildFormationUnlockMap, isFormationUnlocked } from './knowledge-effects';
import { garrisonMaxBonus, ATTACHMENT_UNLOCK_STRUCTURE } from './structure-effects';
import { loadDials, getDialNumber } from '../data/dials';
import { commanderCohortLimit, sortedIds, MINOR_SETTLEMENT_REPAIR_GOLD_COST, MINOR_SETTLEMENT_STRUCTURE_CAP, MINOR_SETTLEMENT_GARRISON_MAX } from './state';
import type { Settlement, SettlementTier } from './state';
import { loadSpecialUnits } from '../data/special-units';

// v0M5.3 (2026-07-25) — SpecialUnits table (Spy at v1). Loaded once at
// module init; consumed by the recruit_spy handler for gold cost +
// prereq lookups.
const SPECIAL_UNITS = loadSpecialUnits();

// v0M3.5 — dials for Phase 1 timer (MFix4.2: no longer read here; the
// autonomous setup-phase clock is set at battle creation in
// `battle-factory.ts`. Left as a reference constant to document that this
// dial exists; consumers should read it via `getDialNumber` where needed.)
const AC_DIALS = loadDials();

// MFix1.4 (2026-07-16) — army-count cap + creation gold cost.
export const MAX_ARMIES_PER_FACTION = getDialNumber(AC_DIALS, 'max_armies_per_faction');
export const ARMY_CREATION_GOLD_COST = getDialNumber(AC_DIALS, 'army_creation_gold_cost');
// v0M10.4 (2026-07-24) — supply deducted when raising a new army; this
// same amount seeds the new army's starting supplies pool (see createArmy).
export const NEW_ARMY_SUPPLY_COST = getDialNumber(AC_DIALS, 'new_army_supply_cost');
// v0M10.1 (2026-07-24) — dials used by the add_army_attachment gate.
const ATTACHMENT_SLOT_CAP_DEFAULT = getDialNumber(AC_DIALS, 'army_attachment_slot_cap');
const AC_SETTLEMENT_ZOC_RADIUS = getDialNumber(AC_DIALS, 'settlement_zoc_radius');
// MFix12.5 (2026-07-27) — cohort transfer range = zoc_radius / 2 (Euclidean).
// Design lock: two armies must be "within ZoC" for a cohort to physically
// move between them. Half zoc_radius chosen as the transfer threshold per
// Michael's elicitation.
const AC_ZOC_RADIUS_FOR_TRANSFER = getDialNumber(AC_DIALS, 'zoc_radius') / 2;

// v0M9 (2026-07-27) — helper: hostile-in-ZoC check for the
// repair_settlement command-issue gate. Mirrors the check in
// pillages.ts::findLowestIdHostileInZoc so the UI surface + sim agree
// on when repair is startable. Cheaper: returns as soon as one
// hostile is found (no id sort needed at command-issue time).
function hostileArmyInMinorZoc(state: GameState, settlement: Settlement): boolean {
  const province = state.provinces.get(settlement.provinceId);
  if (!province) return false;
  const ownerFactionId = province.ownerFactionId;
  let ownerTeam = -Infinity;
  if (ownerFactionId !== null) {
    const f = state.factions.get(ownerFactionId);
    if (!f) return false;
    ownerTeam = f.teamId;
  }
  const rad = AC_SETTLEMENT_ZOC_RADIUS + 0.5;
  for (const army of state.armies.values()) {
    if (army.currentBattleId !== null) continue;
    const af = state.factions.get(army.ownerFactionId);
    if (!af) continue;
    if (ownerFactionId !== null && af.teamId === ownerTeam) continue;
    const dx = Math.abs(army.position.x - settlement.cell.x);
    const dy = Math.abs(army.position.y - settlement.cell.y);
    if (Math.max(dx, dy) <= rad) return true;
  }
  return false;
}

// v0M4.2.1 — garrison cap per city (BATTLES.md §3.2 recruit rule).
export const GARRISON_MAX_PER_SETTLEMENT = getDialNumber(AC_DIALS, 'garrison_max_per_settlement');

// v0M5.5 (2026-07-25) — Sabotage completion timer + Steal drain-rate
// growth. Fallback to sensible defaults if xlsx has not yet been re-
// authored with the dial rows (pending Michael xlsx work) so tests +
// dev builds don't break. Design targets per Michael 2026-07-25:
// 20-day sabotage completion; steal starts at 10% of settlement gold
// income, +5% every 20 days, capped at 100%.
function acDialOrFallback(name: string, fallback: number): number {
  if (!(name in AC_DIALS)) return fallback;
  try { return getDialNumber(AC_DIALS, name); } catch { return fallback; }
}
export const SABOTAGE_COMPLETION_DAYS = acDialOrFallback('sabotage_completion_days', 20);
export const STEAL_BASE_PCT = acDialOrFallback('steal_base_pct', 0.10);
export const STEAL_INCREMENT_PCT = acDialOrFallback('steal_increment_pct', 0.05);
export const STEAL_INCREMENT_DAYS = acDialOrFallback('steal_increment_days', 20);

// v0M7.3 (2026-07-25) — World-buildable dials per MAP.md §9.4.
// Michael-owed additions to the Abilities sheet; fallbacks below match
// the design-lock values so sim runs without an xlsx re-build.
export const WORLD_BUILD_ARRIVAL_EPSILON = acDialOrFallback('world_build_arrival_epsilon', 0.4);
export const WORLD_ROAD_MOVE_MULTIPLIER = acDialOrFallback('world_road_move_multiplier', 0.5);
export const WORLD_BRIDGE_MOVE_MULTIPLIER = acDialOrFallback('world_bridge_move_multiplier', 1.0);
// v0M7.5b (2026-07-26) — per-cell timer for polyline Road construction
// per MAP.md §9.1. Total build time = cell count × this dial + walk time
// between cells.
export const ROAD_BUILD_TIME_PER_CELL_DAYS = acDialOrFallback('road_build_time_per_cell_days', 5);
// v0M6.3 (2026-07-26) — maximum concurrent items in a faction's
// research queue (excluding the currently active item). Bounds player
// pre-planning + prevents pathological "queue everything" state that
// invalidates strategic decisions later.
export const RESEARCH_QUEUE_CAP = acDialOrFallback('research_queue_cap', 5);
// v0M7.4d+siege2 (2026-07-26) — days a Fort holds under siege before
// surrendering to the sieger. Peer of the settlement `siege_surrender_days`
// dial but scoped to Forts so Michael can tune them independently
// (Forts have no population/starvation, so the surrender curve is a
// pure design lever). Consumed by `sieges.ts` fort-siege pass.
export const FORT_SURRENDER_DAYS = acDialOrFallback('fort_surrender_days', 20);

/** MFix1.4: count armies owned by a faction. Excludes armies queued for
 *  disband — there is no such queue; disband is immediate — but we do
 *  count armies that are currently in a battle (still "raised"). */
export function countFactionArmies(state: GameState, factionId: import('./state').FactionId): number {
  let n = 0;
  for (const a of state.armies.values()) {
    if (a.ownerFactionId === factionId) n++;
  }
  return n;
}

/**
 * v0M4.2.1 — count garrison cohorts currently in a city (existing +
 * in-flight garrison recruits queued anywhere for this city). Used as
 * the cap check at recruit-command validation (`garrison_max_per_settlement`
 * dial per BATTLES.md §3.2).
 */
export function countCityGarrison(state: GameState, settlementId: import('./state').SettlementId): number {
  const city = state.settlements.get(settlementId);
  if (!city) return 0;
  let n = city.garrisonCohortIds.length;
  for (const p of state.provinces.values()) {
    for (const order of p.recruitQueue) {
      if (order.settlementId === settlementId) n++;
    }
  }
  return n;
}

// Balance data loaded once at module init — same pattern as SG.
// The Structures blob is deterministic per xlsx build; treating it as
// module-scope-constant simplifies the systemFn contract.
const STRUCTURES = loadStructures();
const COHORTS = loadCohorts();
// v0M9.4 (2026-07-24) — Knowledge tree loaded once at module init;
// consulted by the start_research handler for spec + prereq validation.
const KNOWLEDGE = loadKnowledge();

/**
 * v0M9.12 (2026-07-24) — Static map from structure name → the knowledge
 * whose "Unlocks Structure: X" effect gates it. Consumed by the
 * build_structure handler as a research gate (belt-and-suspenders vs
 * the client-side UI gate in settlement-panel.ts).
 *
 * Regex must stay in sync with the client-side parser — same phrasing,
 * same capture group.
 */
const UNLOCKS_STRUCTURE_RE = /Unlocks\s+Structure:\s*(.+)/i;
const STRUCTURE_UNLOCK_KNOWLEDGE: Map<string, string> = (() => {
  const map = new Map<string, string>();
  for (const knowledgeName of Object.keys(KNOWLEDGE)) {
    const spec = KNOWLEDGE[knowledgeName]!;
    const m = UNLOCKS_STRUCTURE_RE.exec(spec.effects);
    if (m && m[1]) map.set(m[1].trim(), knowledgeName);
  }
  return map;
})();

// v0M9.14 (2026-07-24) — Formation unlock map for the set_battle_formation
// research gate. Client-side lookup in battle-command-modal mirrors.
const FORMATION_UNLOCK_MAP = buildFormationUnlockMap(KNOWLEDGE);

/** Structure caps per tier from ECONOMY.md §2.1 (v0M1.9.16 lock). */
const STRUCTURE_CAP_BY_TIER: Record<SettlementTier, number> = {
  Village: 5,
  Town: 10,
  City: 20,
};

export function applyCommands(
  state: GameState,
  commands: readonly Command[],
  _rng: RngState,
  context: SimContext,
): GameState {
  for (const cmd of commands) {
    switch (cmd.type) {
      case 'noop':
        break;

      case 'move_army': {
        const army = state.armies.get(cmd.armyId);
        if (!army) break; // Rejected: unknown army
        if (army.ownerFactionId !== cmd.playerFactionId) break; // Rejected: not owner
        const { width, height } = context.terrain;
        if (
          cmd.targetX < 0 ||
          cmd.targetY < 0 ||
          cmd.targetX >= width ||
          cmd.targetY >= height
        ) {
          break; // Rejected: out of bounds
        }

        const goalX = Math.floor(cmd.targetX);
        const goalY = Math.floor(cmd.targetY);

        // MFix12.4 (2026-07-27) — append mode: queue the target for later
        // consumption instead of replacing the active goal. Only valid
        // when the army is actively moving toward something; if the army
        // is idle, fall through to the regular non-append branch so the
        // first waypoint starts immediately.
        if (cmd.append === true && army.movementGoal !== null) {
          const queue = army.waypoints ? army.waypoints.slice() : [];
          queue.push({ x: goalX, y: goalY });
          army.waypoints = queue;
          break;
        }

        // Non-append (default) — clear any pending chain so this order
        // preempts a queued sequence.
        if (army.waypoints !== undefined) {
          army.waypoints = undefined;
        }

        // Round current position to the nearest cell for the path start;
        // the pathfinder operates on integer cells, but Army positions
        // are floats — waypoint 0 is the first cell the army will move
        // toward.
        const startX = Math.round(army.position.x);
        const startY = Math.round(army.position.y);

        const path = hierarchicalPath(
          context.terrain,
          context.regionGraph,
          startX,
          startY,
          goalX,
          goalY,
          computeBridgeCells(state, context.terrain.width),
          WORLD_BRIDGE_MOVE_MULTIPLIER,
        );

        if (!path.found) {
          // No path — reject silently. The Army keeps whatever it was
          // doing (idle, or previous plannedPath). The UI will surface
          // rejection to the player when we wire that surface at v0M3.1
          // close.
          break;
        }

        // Adopt the smoothed waypoints. Path start (waypoint 0) is the
        // rounded current cell; drop it so the first waypoint the
        // movement system sees is a real destination the Army isn't
        // already on.
        const smoothed = path.smoothedCells;
        const waypoints = smoothed.length > 1
          ? smoothed.slice(1)
          : smoothed.slice();

        army.plannedPath = waypoints;
        army.movementGoal = { x: goalX, y: goalY };
        break;
      }

      case 'build_structure': {
        // Validate: province exists, is owned by the commanding faction,
        // has cap headroom, and the faction can afford the Gold cost.
        // On success: deduct Gold + queue the structure with progressSec 0.
        const province = state.provinces.get(cmd.provinceId);
        if (!province) break; // Rejected: unknown province
        if (province.ownerFactionId !== cmd.playerFactionId) break; // Rejected: not owner
        const spec = STRUCTURES[cmd.structureName];
        if (!spec) break; // Rejected: unknown structure

        // v0M9-followup.1 (2026-07-27) — Minor settlement attribution.
        // If cmd.settlementId is set, the build routes to that minor —
        // slot cap = MINOR_SETTLEMENT_STRUCTURE_CAP, slot counting is
        // scoped to structures + buildQueue entries tagged with this
        // settlementId. Absent = capital (existing path).
        const targetSettlementId = cmd.settlementId ?? undefined;
        if (targetSettlementId !== undefined) {
          const minor = state.settlements.get(targetSettlementId);
          if (!minor) break; // Rejected: unknown settlement
          if (!minor.isMinor) break; // Rejected: settlementId not a minor
          if (minor.provinceId !== province.id) break; // Rejected: minor not in this province
          const cap = minor.structureCap ?? MINOR_SETTLEMENT_STRUCTURE_CAP;
          let hereCount = 0;
          for (const s of province.structures) if (s.settlementId === targetSettlementId) hereCount++;
          for (const q of province.buildQueue) if (q.settlementId === targetSettlementId) hereCount++;
          if (hereCount >= cap) break; // Rejected: minor slot cap reached
        } else {
          // Capital: tier-based cap over the province total (excludes minor-attributed).
          const cap = STRUCTURE_CAP_BY_TIER[province.tier];
          let capitalCount = 0;
          for (const s of province.structures) if (s.settlementId === undefined) capitalCount++;
          for (const q of province.buildQueue) if (q.settlementId === undefined) capitalCount++;
          if (capitalCount >= cap) break; // Rejected: tier cap reached
        }

        // Gold check on the commanding faction's ledger.
        const faction = state.factions.get(cmd.playerFactionId);
        if (!faction) break; // Rejected: unknown faction
        if (faction.resources.gold < spec.goldCost) break; // Rejected: insufficient Gold

        // v0M9.12 (2026-07-24) — research gate. If any knowledge has an
        // "Unlocks Structure: X" effect targeting this structure, the
        // faction must have researched that knowledge before building.
        // Belt-and-suspenders: UI already locks the button, but this
        // survives dev-console bypass and hostile clients under MP.
        // Test-fixture safe: none of the base 6 structures have unlock
        // knowledge, so pre-v0M9 tests that don't set researchedKnowledge
        // are unaffected.
        const requiredKnowledge = STRUCTURE_UNLOCK_KNOWLEDGE.get(cmd.structureName);
        if (requiredKnowledge && !(faction.researchedKnowledge ?? []).includes(requiredKnowledge)) {
          break; // Rejected: unlock knowledge not researched
        }

        // Commit: deduct Gold, append queue entry with 0 progress + attribution.
        faction.resources.gold -= spec.goldCost;
        province.buildQueue.push({
          name: cmd.structureName,
          progressSec: 0,
          settlementId: targetSettlementId,
        });
        break;
      }

      case 'recruit_cohort': {
        // Validate + queue a cohort recruitment per ECONOMY.md §4 +
        // BATTLES.md §2.7.8.7.d recruitment gate.
        const province = state.provinces.get(cmd.provinceId);
        if (!province) break; // Rejected: unknown province
        if (province.ownerFactionId !== cmd.playerFactionId) break; // Rejected: not owner

        // v0M4.2.1: mutual-exclusion invariant on armyId / settlementId.
        // v0M7.4d+garrison (2026-07-26): extended to include worldStructureId
        // (Fort target). Exactly one of the three targets is non-null (or all
        // null for a new-army recruit).
        const fortTargetId = cmd.worldStructureId ?? null;
        const targetsSet = [cmd.armyId, cmd.settlementId, fortTargetId].filter((t) => t !== null).length;
        if (targetsSet > 1) break; // Rejected: ill-formed multi-target

        // Garrison recruits (settlement or Fort) skip the tier gate. Army
        // recruits still require Town/Settlement per ECONOMY.md §4.
        const isSettlementGarrisonRecruit = cmd.settlementId !== null;
        const isFortGarrisonRecruit = fortTargetId !== null;
        const isGarrisonRecruit = isSettlementGarrisonRecruit || isFortGarrisonRecruit;
        if (!isGarrisonRecruit && province.tier === 'Village') break; // Rejected: army recruit needs Town+

        const spec = COHORTS[cmd.cohortType];
        if (!spec) break; // Rejected: unknown cohort type

        // Structure prerequisites — all must be present as completed structures
        // in the recruiting province (queued-but-not-built doesn't count).
        // Garrison at v1 has empty prereq list so this loop trivially passes.
        let prereqsMet = true;
        for (const prereq of spec.structurePrereq) {
          if (!province.structures.some((s) => s.name === prereq)) {
            prereqsMet = false;
            break;
          }
        }
        if (!prereqsMet) break; // Rejected: structure prereq missing

        const faction = state.factions.get(cmd.playerFactionId);
        if (!faction) break; // Rejected: unknown faction

        // v0M4.2.1: garrison-recruit validation for Settlement target.
        // Target city must exist, must live in the recruiting province
        // (garrisons recruit at their home city, not across provinces),
        // and must have cap headroom. v0M9-followup.3 (2026-07-27):
        // minor settlements use MINOR_SETTLEMENT_GARRISON_MAX cap; only
        // Garrison-type recruits are allowed at minors (defensive-only).
        if (isSettlementGarrisonRecruit) {
          const city = state.settlements.get(cmd.settlementId!);
          if (!city) break; // Rejected: unknown city
          if (city.provinceId !== province.id) break; // Rejected: city not in this province
          if (city.isMinor) {
            // Only Garrison cohorts at minors — Armies are recruited at
            // Province Capitals per MECHANICS.md §3.1.
            if (cmd.cohortType !== 'Garrison') break; // Rejected: non-garrison at minor
            const cap = MINOR_SETTLEMENT_GARRISON_MAX;
            if (countCityGarrison(state, cmd.settlementId!) >= cap) {
              break; // Rejected: minor garrison cap reached
            }
          } else {
            // v0M9.13 (2026-07-24) — effective garrison cap = base dial +
            // Garrison Maximum +N bonuses from built structures in this
            // province (Garrison Quarters at v1).
            const effectiveGarrisonCap = GARRISON_MAX_PER_SETTLEMENT + garrisonMaxBonus(province, STRUCTURES);
            if (countCityGarrison(state, cmd.settlementId!) >= effectiveGarrisonCap) {
              break; // Rejected: garrison cap reached
            }
          }
        }

        // v0M7.4d+garrison (2026-07-26) — Fort-target garrison-recruit
        // validation. Fort must exist + be owned by issuing faction +
        // have cap headroom + sit within the recruiting province's
        // border. Reuses GARRISON_MAX_PER_SETTLEMENT for now — a
        // dedicated `fort_garrison_cap` dial can differentiate later.
        if (isFortGarrisonRecruit) {
          const fort = state.worldStructures?.get(fortTargetId!);
          if (!fort) break; // Rejected: unknown Fort
          if (fort.kind !== 'fort') break; // Rejected: target isn't a Fort
          if (fort.ownerFactionId !== cmd.playerFactionId) break; // Rejected: not owner
          // Fort must sit inside the recruiting province.
          if (context.provinceMap) {
            const idx = fort.cell.y * context.terrain.width + fort.cell.x;
            const fortProvinceId = context.provinceMap[idx];
            if (fortProvinceId !== province.id) break; // Rejected: Fort not in this province
          }
          // Cap: reuse settlement garrison cap default. `garrisonCohortIds`
          // is the source of truth for current fill.
          const currentFortGarrison = (fort.garrisonCohortIds ?? []).length;
          if (currentFortGarrison >= GARRISON_MAX_PER_SETTLEMENT) {
            break; // Rejected: fort garrison cap reached
          }
        }

        // MFix1.4: creating a new army (armyId === null AND settlementId === null)
        // costs an extra ARMY_CREATION_GOLD_COST and is capped at
        // MAX_ARMIES_PER_FACTION. Attaching to an existing army or
        // recruiting a garrison incurs no extra cost + no army cap.
        const isNewArmy = cmd.armyId === null && cmd.settlementId === null;
        const extraGold = isNewArmy ? ARMY_CREATION_GOLD_COST : 0;
        // v0M10.4 (2026-07-24) — new armies also cost NEW_ARMY_SUPPLY_COST
        // in supplies. Deducted from faction.resources.supplies below;
        // the same amount seeds the new army's starting supplies pool
        // (createArmy defaults to 600).
        const extraSupplies = isNewArmy ? NEW_ARMY_SUPPLY_COST : 0;
        if (isNewArmy) {
          // Count includes armies currently in-battle. Pending-recruit
          // orders for future new armies also count so a spam of queued
          // new-army recruits can't blow past the cap the same tick they
          // land — recruitQueue entries with armyId === null AND settlementId === null
          // are the "raise-a-new-army" orders in flight. v0M4.2.1: garrison
          // recruits (settlementId !== null) do not count against the army cap.
          let queuedNewArmies = 0;
          for (const p of state.provinces.values()) {
            for (const order of p.recruitQueue) {
              if (order.armyId === null && order.settlementId === null && p.ownerFactionId === cmd.playerFactionId) {
                queuedNewArmies++;
              }
            }
          }
          // v0M9.9 (2026-07-24) — effective cap = base dial + sum of
          // "Max Army +N" bonuses from researched knowledges (Command
          // and Control chain, and any future rows with the same effect
          // phrasing). See src/sim/knowledge-effects.ts.
          const effectiveArmyCap = MAX_ARMIES_PER_FACTION + maxArmyBonus(faction, KNOWLEDGE);
          if (countFactionArmies(state, cmd.playerFactionId) + queuedNewArmies >= effectiveArmyCap) {
            break; // Rejected: army cap reached
          }
        }

        // Faction resource sufficiency (cohort cost + optional new-army gold + optional new-army supplies).
        if (faction.resources.gold < spec.goldCost + extraGold) break;
        if (faction.resources.iron < spec.ironCost) break;
        if (faction.resources.wood < spec.woodCost) break;
        if (faction.resources.horses < spec.horsesCost) break;
        if (faction.resources.supplies < spec.suppliesCost + extraSupplies) break;

        // MFix6.6.3 (2026-07-23) — Manpower gating. Recruitment now draws
        // from the recruiting city's manpower pool at 1 manpower per
        // figure the cohort fields. Population is no longer touched
        // (population continues to grow independently; manpower is a
        // separate pool fed by population at generation rate).
        //
        // Which city's pool?
        //   - Garrison recruit (cmd.settlementId !== null): the target city.
        //   - Army recruit (cmd.settlementId === null): the first city in the
        //     recruiting province (v1 rule: one major city per province).
        //
        // Race match: for now v1 cohorts are all 'Human' and all city
        // pools are 'Human', so the check is inert but future-proofs
        // the fantasy expansion.
        const cohortManpowerRace = 'Human'; // v1 default; post-MVP: from cmd or spec
        let recruitingCity: import('./state').Settlement | null = null;
        if (isSettlementGarrisonRecruit) {
          const targetCity = state.settlements.get(cmd.settlementId!) ?? null;
          if (targetCity && targetCity.isMinor) {
            // v0M9-followup.3 (2026-07-27) — minors don't carry their own
            // manpower pool; garrison recruit draws from the Province Capital.
            for (const c of state.settlements.values()) {
              if (c.provinceId === province.id && !c.isMinor) { recruitingCity = c; break; }
            }
          } else {
            recruitingCity = targetCity;
          }
        } else {
          // Army recruits AND Fort-garrison recruits (v0M7.4d+garrison)
          // both draw manpower from the province's first non-minor city
          // (Province Capital).
          for (const c of state.settlements.values()) {
            if (c.provinceId === province.id && !c.isMinor) { recruitingCity = c; break; }
          }
        }
        if (!recruitingCity) break; // Rejected: no city in recruiting province
        const cityRace = recruitingCity.manpowerRace ?? 'Human';
        if (cityRace !== cohortManpowerRace) break; // Rejected: race mismatch
        const manpowerCost = spec.figureCount; // 1 manpower per figure
        const currentManpower = recruitingCity.manpower ?? 0;
        if (currentManpower < manpowerCost) break; // Rejected: insufficient manpower

        // Commander cohort limit if attaching to an existing army.
        if (cmd.armyId !== null) {
          const army = state.armies.get(cmd.armyId);
          if (!army) break; // Rejected: unknown army
          if (army.ownerFactionId !== cmd.playerFactionId) break; // Rejected: not owner
          // v0M3.12: existing army must be inside the recruiting province's
          // border to receive recruits. Reads context.provinceMap (skipped
          // for test fixtures without it, where army positions may be
          // synthetic). Michael's playtest report: "recruit to existing
          // army silently fails" — was actually the (0, 0) spawn bug on
          // the NEW-army path, but the border rule is an intentional
          // v1 lock so recruits stage from a recruit centre, not just
          // anywhere the army happens to be.
          if (!armyInProvince(context, army, province.id)) break; // Rejected: army not in recruiting province
          const commander = state.commanders.get(army.commanderId);
          if (!commander) break; // Rejected: dangling commanderId
          const limit = commanderCohortLimit(commander.level);
          // Include cohorts already in queue for this army from any province.
          let inQueueForThisArmy = 0;
          for (const provinceId of state.provinces.keys()) {
            const p = state.provinces.get(provinceId)!;
            for (const order of p.recruitQueue) {
              if (order.armyId === cmd.armyId) inQueueForThisArmy++;
            }
          }
          if (army.cohortIds.length + inQueueForThisArmy >= limit) break; // Rejected: commander limit
        }

        // Commit: deduct resources + manpower, append queue entry.
        // MFix1.4: extraGold (new-army creation cost) added to gold deduction.
        // MFix6.6.3 (2026-07-23): population no longer deducted — cohorts
        // draw from the recruiting city's manpower pool (1 manpower per
        // figure). Population continues to grow untouched by recruitment.
        faction.resources.gold -= spec.goldCost + extraGold;
        faction.resources.iron -= spec.ironCost;
        faction.resources.wood -= spec.woodCost;
        faction.resources.horses -= spec.horsesCost;
        // v0M10.4: new-army supply cost paid at command time; the
        // deducted amount is what seeds the new army's supplies pool
        // when it spawns (see createArmy default = 600).
        faction.resources.supplies -= spec.suppliesCost + extraSupplies;
        recruitingCity.manpower = currentManpower - manpowerCost;
        province.recruitQueue.push({
          cohortType: cmd.cohortType,
          armyId: cmd.armyId,
          settlementId: cmd.settlementId,
          // v0M7.4d+garrison (2026-07-26) — carry Fort target through to
          // spawnCohort branch 4. undefined for non-Fort recruits.
          worldStructureId: fortTargetId,
          progressSec: 0,
        });
        break;
      }

      case 'enter_battle_command': {
        // v0M3.5 — player takes command of a battle. Guard: battle
        // exists, is not concluded, and the issuing faction owns at
        // least one of the participating armies.
        const battle = state.battles.get(cmd.battleId);
        if (!battle) break;
        if (battle.result !== null) break;
        const [aId, bId] = battle.armyIds;
        const armyA = state.armies.get(aId);
        const armyB = state.armies.get(bId);
        const isParticipant =
          (armyA && armyA.ownerFactionId === cmd.playerFactionId) ||
          (armyB && armyB.ownerFactionId === cmd.playerFactionId);
        if (!isParticipant) break;
        // MFix4.2 — the deployment/setup phase now runs autonomously
        // from battle creation (see `battle-factory.ts` — phase1EndTick
        // is set at creation, not on enter). Opening battle-command
        // mid-round no longer resets the clock; the player just sees
        // whatever remaining setup window is left. `autoAI` still flips
        // for UI-only "player has taken command" indication.
        battle.autoAI = false;
        break;
      }

      case 'exit_battle_command': {
        // v0M3.5 — player closes the modal. MFix4.2: sim was NEVER
        // paused for the modal (that changed in MFix4.2 — deployment
        // phase runs autonomously); only the `autoAI` UI-flag flips
        // back so future re-opens read "not commanded" correctly.
        const battle = state.battles.get(cmd.battleId);
        if (!battle) break;
        // Same participation check for symmetry.
        const [aId, bId] = battle.armyIds;
        const armyA = state.armies.get(aId);
        const armyB = state.armies.get(bId);
        const isParticipant =
          (armyA && armyA.ownerFactionId === cmd.playerFactionId) ||
          (armyB && armyB.ownerFactionId === cmd.playerFactionId);
        if (!isParticipant) break;
        battle.autoAI = true;
        // MFix4.2: don't clear phase1EndTick — it lives as the
        // autonomous setup-phase clock and reads correctly whether the
        // modal is open or closed. It expires naturally at
        // `state.tick >= phase1EndTick` via battles.ts / battle-round.ts.
        break;
      }

      case 'end_setup_phase': {
        // MFix5+fix4 (2026-07-23) — player-elective early-end of the
        // Setup phase. Only accepted from a participant faction and
        // only while the Setup window is still open (no-op otherwise).
        const battle = state.battles.get(cmd.battleId);
        if (!battle) break;
        if (battle.result !== null) break;
        // Participant check.
        const [aId, bId] = battle.armyIds;
        const armyA = state.armies.get(aId);
        const armyB = state.armies.get(bId);
        const isParticipant =
          (armyA && armyA.ownerFactionId === cmd.playerFactionId) ||
          (armyB && armyB.ownerFactionId === cmd.playerFactionId);
        if (!isParticipant) break;
        // Only meaningful during Setup.
        if (battle.phase1EndTick === null) break;
        if (state.tick >= battle.phase1EndTick) break;
        // Skip Setup: mark it as ended this tick. `battles.ts` will
        // clear `phase1EndTick` + reset `phaseStartTick` on the next
        // tick (setting phase1EndTick = state.tick puts us at the exact
        // expiry threshold; the `state.tick >= battle.phase1EndTick`
        // branch fires).
        battle.phase1EndTick = state.tick;
        break;
      }

      case 'set_cohort_move': {
        // v0M3.5 — player sets a cohort's moveOrder target square.
        const battle = state.battles.get(cmd.battleId);
        if (!battle) break;
        if (battle.result !== null) break;
        // Find the cohort. It lives in one of the sides.
        for (const side of battle.sides) {
          const cof = side.cohorts.get(cmd.cohortId);
          if (!cof) continue;
          // Ownership check: the cohort's faction must be the issuing faction.
          if (cof.factionId !== cmd.playerFactionId) break;
          // Bounds check on the target square (20×20 battlescape).
          if (cmd.targetSquare.x < 0 || cmd.targetSquare.x >= 20) break;
          if (cmd.targetSquare.y < 0 || cmd.targetSquare.y >= 20) break;
          // During Phase 1 the player can reposition inside their own
          // deployment zone. Otherwise treat as a moveOrder for phaseMovement.
          const inPhase1 = battle.phase1EndTick !== null && state.tick < battle.phase1EndTick;
          if (inPhase1) {
            // Deployment zone: side 0 = rows 15-19, side 1 = rows 0-4.
            const zoneOk =
              side.sideIndex === 0
                ? cmd.targetSquare.y >= 15 && cmd.targetSquare.y <= 19
                : cmd.targetSquare.y >= 0 && cmd.targetSquare.y <= 4;
            if (!zoneOk) break;
            // Occupancy check: no other cohort at that square.
            let occupied = false;
            for (const other of side.cohorts.values()) {
              if (other.id === cof.id) continue;
              if (other.position.x === cmd.targetSquare.x && other.position.y === cmd.targetSquare.y) {
                occupied = true;
                break;
              }
            }
            if (occupied) break;
            // Reposition — clears any pending moveOrder.
            cof.position = { x: cmd.targetSquare.x, y: cmd.targetSquare.y };
            cof.startSquare = cof.position;
            cof.moveOrder = null;
          } else {
            // Regular moveOrder — phaseMovement will step toward it.
            cof.moveOrder = { targetSquare: cmd.targetSquare };
          }
          break;
        }
        break;
      }

      case 'set_cohort_order': {
        // v0M3.5 — player sets a cohort's standingOrder.
        const battle = state.battles.get(cmd.battleId);
        if (!battle) break;
        if (battle.result !== null) break;
        for (const side of battle.sides) {
          const cof = side.cohorts.get(cmd.cohortId);
          if (!cof) continue;
          if (cof.factionId !== cmd.playerFactionId) break;
          cof.standingOrder = cmd.standingOrder;
          break;
        }
        break;
      }

      case 'set_battle_formation': {
        // v0M4.0c — player picks a formation template. Repositions cohorts
        // on the target side per the template's layout algorithm. Gate:
        // Phase 1 only (post-Phase-1 formation is frozen — combat begins).
        // Ownership: player must own an army on the target side.
        const battle = state.battles.get(cmd.battleId);
        if (!battle) break;
        if (battle.result !== null) break;
        // Phase 1 gate.
        const inPhase1 = battle.phase1EndTick !== null && state.tick < battle.phase1EndTick;
        if (!inPhase1) break;
        const side = battle.sides[cmd.sideIndex];
        if (!side) break;
        // Ownership: at least one cohort on the target side must belong
        // to the issuing faction. (Prevents opposing players from
        // reshuffling each other's cohorts in future MP.)
        let ownsSide = false;
        for (const cof of side.cohorts.values()) {
          if (cof.factionId === cmd.playerFactionId) { ownsSide = true; break; }
        }
        if (!ownsSide) break;
        // v0M9.14 (2026-07-24) — research gate. Look up humanized name
        // in the static unlock map; reject if gated and unresearched.
        const faction = state.factions.get(cmd.playerFactionId);
        if (!faction) break;
        const humanized = cmd.template.startsWith('Formation')
          ? cmd.template.slice('Formation'.length).replace(/([a-z])([A-Z])/g, '$1 $2')
          : cmd.template;
        if (!isFormationUnlocked(humanized, faction, FORMATION_UNLOCK_MAP)) break;
        // v0M4.0j.1: pass battle terrain so formation placements shift off
        // impassable cells via spiral search (§2.8.6.4).
        applyFormation(side, cmd.template, battle.terrain);
        // v0M4.1.1 — log the formation cascade for the combat log.
        battle.log.push({
          kind: 'formation-cascade',
          tick: state.tick,
          round: battle.round,
          sideIndex: cmd.sideIndex,
          template: cmd.template,
        });
        break;
      }

      case 'assault_settlement': {
        // v0M4.2.5 — spawn a siege battle from the attacker army against
        // the city it is currently besieging (BATTLES.md §3.5). Per §3.3:
        //   - Army must exist + be owned by the issuing faction.
        //   - Army must not already be in a battle (engagement lock).
        //   - Some city must have this army as `siegeState.siegingArmyId`.
        // On success: `createSiegeBattleFromArmies` sets the engagement
        // lock + registers the battle in `state.battles`.
        const army = state.armies.get(cmd.armyId);
        if (!army) break;
        if (army.ownerFactionId !== cmd.playerFactionId) break;
        if (army.currentBattleId !== null) break; // Already fighting.
        // Find the city this army is besieging.
        let besiegedCity: import('./state').Settlement | null = null;
        for (const city of state.settlements.values()) {
          if (city.siegeState !== null && city.siegeState.siegingArmyId === army.id) {
            besiegedCity = city;
            break;
          }
        }
        if (!besiegedCity) break; // No open siege — nothing to assault.
        // v0M13.1 (2026-07-30) — reject assault by a zero-eligible attacker.
        // Prevents the player from spawning a battle with an army that would
        // enter with zero cohorts on the field. Silent — the player retains
        // the army and can rest it before trying again.
        if (!armyHasEligibleCohorts(army, state)) break;
        // Optional defender field army — for v0M4.2.5 first pass, garrison
        // only. v0M4.2.2's siege-open check already precluded siege on a
        // province with a defender field army; if one arrived mid-siege
        // the siege was cleared. So `defenderArmy = null` here is
        // consistent with §3.5. When defender-field-army-mid-siege is
        // supported, look up that army here.
        const battle = createSiegeBattleFromArmies(state, army, besiegedCity, null, context);
        state.battles.set(battle.id, battle);
        break;
      }

      case 'assault_fort': {
        // v0M7.4d+siege (2026-07-26) — spawn a Fort-assault battle from
        // the attacker army against an adjacent enemy Fort per MAP.md
        // §9.2. Direct-assault only (no siege state machine); battle
        // triggers the moment the player issues the command from an
        // adjacent land cell. Per session-33 elicited scope:
        //   - Army must exist + be owned by issuing faction.
        //   - Army must not already be in another battle.
        //   - Fort must exist + be enemy-owned.
        //   - Army must sit within Chebyshev distance 1 of the Fort
        //     cell (adjacent OR on-cell — on-cell only reachable if
        //     the Fort was captured out from under a garrisoning army).
        // On success: `createFortAssaultBattleFromArmies` sets the
        // engagement lock + registers the battle. Battlemap template
        // resolves from category 'fort_walled' (Michael-authored
        // FortAssault* rows in BattleMap.xlsx).
        const army = state.armies.get(cmd.armyId);
        if (!army) break;
        if (army.ownerFactionId !== cmd.playerFactionId) break;
        if (army.currentBattleId !== null) break;
        const fort = state.worldStructures?.get(cmd.fortId);
        if (!fort) break;
        if (fort.kind !== 'fort') break;
        if (fort.ownerFactionId === cmd.playerFactionId) break; // Not enemy.
        // Chebyshev-1 adjacency check.
        const dx = Math.abs(Math.floor(army.position.x) - fort.cell.x);
        const dy = Math.abs(Math.floor(army.position.y) - fort.cell.y);
        if (Math.max(dx, dy) > 1) break; // Not adjacent.
        // v0M13.1 (2026-07-30) — entry gate. Reject if attacker has no
        // eligible cohorts to field.
        if (!armyHasEligibleCohorts(army, state)) break;
        const battle = createFortAssaultBattleFromArmies(state, army, fort, context);
        state.battles.set(battle.id, battle);
        break;
      }

      case 'set_commander_order': {
        // v0M4.0d — player issues an army-level Commander's Order to a
        // side. Cascades to per-cohort standingOrders per §2.8.2 map.
        // Gate: Phase 2 only per v0M4.0d design lock (2026-07-15) —
        // Commander's Orders take effect once combat starts; Phase 1 is
        // for repositioning + formation picking. PLAYTEST OPEN whether
        // Phase 1 should also accept them.
        const battle = state.battles.get(cmd.battleId);
        if (!battle) break;
        if (battle.result !== null) break;
        const inPhase1 = battle.phase1EndTick !== null && state.tick < battle.phase1EndTick;
        if (inPhase1) break; // Reject during setup window.
        const side = battle.sides[cmd.sideIndex];
        if (!side) break;
        let ownsSide = false;
        for (const cof of side.cohorts.values()) {
          if (cof.factionId === cmd.playerFactionId) { ownsSide = true; break; }
        }
        if (!ownsSide) break;
        cascadeCommanderOrder(side, cmd.order);
        // v0M4.1.1 — log the commander-order cascade.
        battle.log.push({
          kind: 'commander-order',
          tick: state.tick,
          round: battle.round,
          sideIndex: cmd.sideIndex,
          order: cmd.order,
        });
        break;
      }

      case 'add_army_attachment': {
        // v0M10.1 (2026-07-24) — add an attachment to an army. Validated:
        //   1. Army exists + owned by issuing faction.
        //   2. Army not in a battle.
        //   3. Attachment type is known (present in ATTACHMENT_UNLOCK_STRUCTURE).
        //   4. Attachment slot free.
        //   5. Army sits in a friendly settlement's ZoC.
        //   6. That settlement's province has the required structure built.
        // On success: appends attachmentType to army.attachments. No
        // resource cost at v1 (Michael can add if playtest wants gating).
        const army = state.armies.get(cmd.armyId);
        if (!army) break;
        if (army.ownerFactionId !== cmd.playerFactionId) break;
        if (army.currentBattleId !== null) break;
        const requiredStructure = ATTACHMENT_UNLOCK_STRUCTURE[cmd.attachmentType];
        if (!requiredStructure) break; // Unknown attachment type
        const currentAttachments = army.attachments ?? [];
        const cap = army.attachmentCap ?? ATTACHMENT_SLOT_CAP_DEFAULT;
        if (currentAttachments.length >= cap) break; // No slot
        const faction = state.factions.get(cmd.playerFactionId);
        if (!faction) break;
        // Find a friendly settlement within ZoC that has the required
        // structure built. Iterate settlements via sortedIds for determinism.
        let unlocked = false;
        for (const settlementId of sortedIds(state.settlements)) {
          const settlement = state.settlements.get(settlementId);
          if (!settlement) continue;
          const province = state.provinces.get(settlement.provinceId);
          if (!province) continue;
          if (province.ownerFactionId !== cmd.playerFactionId) continue;
          // Distance check (Chebyshev — matches other ZoC helpers).
          const dx = Math.abs(army.position.x - settlement.cell.x);
          const dy = Math.abs(army.position.y - settlement.cell.y);
          if (Math.max(dx, dy) > AC_SETTLEMENT_ZOC_RADIUS) continue;
          // Structure gate.
          if (!province.structures.some((s) => s.name === requiredStructure)) continue;
          unlocked = true;
          break;
        }
        if (!unlocked) break; // No qualifying settlement in ZoC
        // Commit. Materialize the array if the army predates v0M10.
        if (!army.attachments) army.attachments = [];
        army.attachments.push(cmd.attachmentType);
        break;
      }

      case 'start_research': {
        // v0M9.4 (2026-07-24) — begin faction-wide research on a knowledge.
        // Validation:
        //   1. Faction exists.
        //   2. Knowledge name resolves against the loaded knowledge tree.
        //   3. Not already researched (append-only researchedKnowledge list).
        //   4. No active research (single-active per v0M9 design lock).
        //   5. Prerequisite (if any) is already researched.
        //   6. Faction Learning ≥ knowledge.learningCost.
        // On success: deduct learningCost + set activeResearch (progressDays=0).
        // Reject silently on any check failure (matches build_structure /
        // recruit_cohort pattern; UI pre-checks show the reason).
        const faction = state.factions.get(cmd.playerFactionId);
        if (!faction) break;
        const spec = KNOWLEDGE[cmd.knowledgeName];
        if (!spec) break;
        const already = faction.researchedKnowledge ?? [];
        if (already.includes(cmd.knowledgeName)) break;
        if ((faction.activeResearch ?? null) !== null) break;
        if (spec.prerequisite !== '' && !already.includes(spec.prerequisite)) break;
        if (faction.resources.learning < spec.learningCost) break;
        faction.resources.learning -= spec.learningCost;
        // Ensure the researchedKnowledge array is materialized on the
        // faction (may be undefined on test fixtures pre-v0M9).
        if (!faction.researchedKnowledge) faction.researchedKnowledge = [];
        faction.activeResearch = {
          knowledgeName: cmd.knowledgeName,
          progressDays: 0,
        };
        break;
      }

      // v0M6.3 (2026-07-26) — append a knowledge to the faction's
      // research queue. Validation mirrors start_research minus the
      // Learning check (deducted at promotion, not enqueue) and the
      // no-active-research check (whole point of the queue). Silently
      // rejects on any failure — client-side UI pre-checks show the
      // reason on the button title.
      case 'enqueue_research': {
        const faction = state.factions.get(cmd.playerFactionId);
        if (!faction) break;
        const spec = KNOWLEDGE[cmd.knowledgeName];
        if (!spec) break;
        const already = faction.researchedKnowledge ?? [];
        if (already.includes(cmd.knowledgeName)) break;
        if ((faction.activeResearch?.knowledgeName ?? null) === cmd.knowledgeName) break;
        const queue = faction.researchQueue ?? [];
        if (queue.includes(cmd.knowledgeName)) break;
        if (queue.length >= RESEARCH_QUEUE_CAP) break;
        // Prereq check — accept if the prereq is researched OR is
        // already queued/active. Lets the player line up a chain
        // (parent + child in same session of enqueue clicks).
        if (spec.prerequisite !== '') {
          const activeName = faction.activeResearch?.knowledgeName ?? null;
          const prereqOk =
            already.includes(spec.prerequisite) ||
            queue.includes(spec.prerequisite) ||
            activeName === spec.prerequisite;
          if (!prereqOk) break;
        }
        if (!faction.researchQueue) faction.researchQueue = [];
        faction.researchQueue.push(cmd.knowledgeName);
        break;
      }

      // v0M6.3 (2026-07-26) — remove a knowledge from the faction's
      // research queue. No Learning refund (queued items never paid
      // — cost is deducted at promotion). No-op if the name isn't
      // in the queue.
      case 'cancel_queued_research': {
        const faction = state.factions.get(cmd.playerFactionId);
        if (!faction) break;
        const queue = faction.researchQueue;
        if (!queue || queue.length === 0) break;
        const idx = queue.indexOf(cmd.knowledgeName);
        if (idx < 0) break;
        queue.splice(idx, 1);
        break;
      }

      // v0M6.3 (2026-07-26) — cancel the in-progress research. Per
      // the session-33 elicited policy (2026-07-26), NO Learning
      // refund — the Learning spent at start remains spent. Any
      // queued items promote naturally on the next research-system
      // tick pass.
      case 'cancel_active_research': {
        const faction = state.factions.get(cmd.playerFactionId);
        if (!faction) break;
        if ((faction.activeResearch ?? null) === null) break;
        faction.activeResearch = null;
        break;
      }

      // v0M9 (2026-07-27) — Repair a pillaged minor settlement.
      // Validation: minor + pillaged + owner match + no repair in flight
      // + no enemy in ZoC + Gold sufficiency. On success: deduct Gold +
      // seed repairState. Gold is NOT refunded on cancel or pause.
      case 'repair_settlement': {
        const settlement = state.settlements.get(cmd.settlementId);
        if (!settlement) break;
        if (!settlement.isMinor) break;
        if (!settlement.pillaged) break;
        if ((settlement.repairState ?? null) != null) break;
        const province = state.provinces.get(settlement.provinceId);
        if (!province) break;
        if (province.ownerFactionId !== cmd.playerFactionId) break;
        // Enemy-in-ZoC gate — mirror the repair.ts pause rule at
        // command-issue time so the UI surface + sim agree.
        if (hostileArmyInMinorZoc(state, settlement)) break;
        const faction = state.factions.get(cmd.playerFactionId);
        if (!faction) break;
        if (faction.resources.gold < MINOR_SETTLEMENT_REPAIR_GOLD_COST) break;

        faction.resources.gold -= MINOR_SETTLEMENT_REPAIR_GOLD_COST;
        settlement.repairState = { daysElapsed: 0 };
        break;
      }

      // v0M9 (2026-07-27) — Cancel an in-progress repair. Ownership
      // check only; Gold not refunded per Michael's design lock (v0M6.3
      // cancel-active-research pattern).
      case 'cancel_repair_settlement': {
        const settlement = state.settlements.get(cmd.settlementId);
        if (!settlement) break;
        if ((settlement.repairState ?? null) == null) break;
        const province = state.provinces.get(settlement.provinceId);
        if (!province) break;
        if (province.ownerFactionId !== cmd.playerFactionId) break;
        settlement.repairState = null;
        break;
      }

      // MFix11.6 (2026-07-27) — Cancel a build-queue entry. No resource
      // refund per Michael's design lock (matches v0M6.3 research-queue).
      case 'cancel_build_structure': {
        const province = state.provinces.get(cmd.provinceId);
        if (!province) break;
        if (province.ownerFactionId !== cmd.playerFactionId) break;
        if (cmd.index < 0 || cmd.index >= province.buildQueue.length) break;
        province.buildQueue.splice(cmd.index, 1);
        break;
      }

      // MFix11.6 (2026-07-27) — Cancel a recruit-queue entry. No refund.
      case 'cancel_recruit_cohort': {
        const province = state.provinces.get(cmd.provinceId);
        if (!province) break;
        if (province.ownerFactionId !== cmd.playerFactionId) break;
        if (cmd.index < 0 || cmd.index >= province.recruitQueue.length) break;
        province.recruitQueue.splice(cmd.index, 1);
        break;
      }

      // MFix12.5 (2026-07-27) — Transfer a cohort between two own-faction
      // armies within zoc_radius/2 (Euclidean). Silent no-op on any
      // validation failure. Non-garrison cohorts only. Neither army in
      // battle. Respects commander cohort cap on the target army.
      case 'transfer_cohort': {
        const fromArmy = state.armies.get(cmd.fromArmyId);
        if (!fromArmy) break;
        const toArmy = state.armies.get(cmd.toArmyId);
        if (!toArmy) break;
        if (cmd.fromArmyId === cmd.toArmyId) break; // Same army — nonsense.
        // Ownership: both must be player's + same faction (redundant given
        // v1 SP but explicit for clarity + post-MVP MP hardening).
        if (fromArmy.ownerFactionId !== cmd.playerFactionId) break;
        if (toArmy.ownerFactionId !== cmd.playerFactionId) break;
        if (fromArmy.ownerFactionId !== toArmy.ownerFactionId) break;
        // Battle lock: neither army in a battle.
        if (fromArmy.currentBattleId !== null || toArmy.currentBattleId !== null) break;
        // Cohort validation.
        const cohort = state.cohorts.get(cmd.cohortId);
        if (!cohort) break;
        if (cohort.armyId !== cmd.fromArmyId) break;
        // Garrison exclusion — settlement- or fort-garrisons never leave.
        if (cohort.settlementId !== null) break;
        if (cohort.worldStructureId !== null && cohort.worldStructureId !== undefined) break;
        // Range: Euclidean distance ≤ AC_ZOC_RADIUS / 2 (dial from
        // MFix12.5 design lock, currently zoc_radius=6 → range 3).
        const dxRng = fromArmy.position.x - toArmy.position.x;
        const dyRng = fromArmy.position.y - toArmy.position.y;
        if (Math.hypot(dxRng, dyRng) > AC_ZOC_RADIUS_FOR_TRANSFER) break;
        // Commander cap on target — refuse if adding would exceed.
        const targetCommander = state.commanders.get(toArmy.commanderId);
        const targetCap = targetCommander !== undefined
          ? commanderCohortLimit(targetCommander.level)
          : 6; // Fallback for tests with no commander wired
        if (toArmy.cohortIds.length + 1 > targetCap) break;
        // All checks pass — mutate.
        const idx = fromArmy.cohortIds.indexOf(cmd.cohortId);
        if (idx < 0) break; // Defensive; should always be found given cohort.armyId check.
        fromArmy.cohortIds.splice(idx, 1);
        toArmy.cohortIds.push(cmd.cohortId);
        cohort.armyId = cmd.toArmyId;
        break;
      }

      // v0M5.3 initial (2026-07-25) — Spy recruit; v0M5.4d amendment —
      // enqueue into settlement.provinceId.recruitQueue with the special-
      // unit discriminator per RecruitmentOrder.specialUnitType. Completion
      // fires from `stepRecruitQueue` in economy.ts once progressSec
      // reaches `recruitTimeDays × TICKS_PER_GAME_DAY × SECONDS_PER_TICK`.
      case 'recruit_spy': {
        const settlement = state.settlements.get(cmd.settlementId);
        if (!settlement) break;
        const province = state.provinces.get(settlement.provinceId);
        if (!province) break;
        if (province.ownerFactionId !== cmd.playerFactionId) break;
        const faction = state.factions.get(cmd.playerFactionId);
        if (!faction) break;
        const spec = SPECIAL_UNITS[cmd.specialUnitType];
        if (!spec) break;
        // Structure prereq: single named structure must be built in the province.
        if (spec.structurePrereq !== '') {
          const has = province.structures.some((s) => s.name === spec.structurePrereq);
          if (!has) break;
        }
        // Learning prereq: single knowledge name (blank at v1 → skip).
        if (spec.learningPrereq !== '') {
          const researched = faction.researchedKnowledge ?? [];
          if (!researched.includes(spec.learningPrereq)) break;
        }
        // Gold cost.
        if (faction.resources.gold < spec.goldCost) break;
        faction.resources.gold -= spec.goldCost;
        // Enqueue. cohortType empty string signals "special-unit recruit";
        // the discriminator is the presence of specialUnitType.
        province.recruitQueue.push({
          cohortType: '',
          armyId: null,
          settlementId: cmd.settlementId,
          specialUnitType: cmd.specialUnitType,
          progressSec: 0,
        });
        break;
      }

      // v0M5.3 (2026-07-25) — Spy move. Mirrors move_army: ownership +
      // in-bounds check, then hierarchicalPath from the Spy's current
      // cell to the target. On success: stamp movementGoal + smoothed
      // waypoints. Spies use the same movement system as armies (see
      // movement.ts — will need extension in a follow-up to iterate
      // spies alongside armies).
      case 'move_spy': {
        const spy = state.spies?.get(cmd.spyId);
        if (!spy) break;
        if (spy.ownerFactionId !== cmd.playerFactionId) break;
        // v0M5.4 — spies inside settlements can't be pathed; player must
        // exit first. (UI can chain exit_settlement_spy → move_spy but
        // the sim rejects a raw move_spy on an inside-spy.)
        if (spy.insideSettlementId) break;

        // MFix12.4 (2026-07-27) — append mode: queue waypoint instead of
        // replacing goal. Only when spy is actively moving.
        if (cmd.append === true && spy.movementGoal !== null) {
          const gx = Math.floor(cmd.targetX);
          const gy = Math.floor(cmd.targetY);
          const queue = spy.waypoints ? spy.waypoints.slice() : [];
          queue.push({ x: gx, y: gy });
          spy.waypoints = queue;
          break;
        }
        // Non-append — clear any pending chain.
        if (spy.waypoints !== undefined) {
          spy.waypoints = undefined;
        }

        // MFix11.2+.3 (2026-07-27) — a manual move overrides any pending
        // escort-and-enter intent. Clear before pathing so the movement
        // system doesn't auto-enter mid-way to the new destination.
        spy.enterOnArrivalSettlementId = null;
        spy.enterOnArrivalOwnerAtIntent = null;
        const { width, height } = context.terrain;
        if (
          cmd.targetX < 0 ||
          cmd.targetY < 0 ||
          cmd.targetX >= width ||
          cmd.targetY >= height
        ) {
          break;
        }
        const startX = Math.round(spy.position.x);
        const startY = Math.round(spy.position.y);
        const goalX = Math.floor(cmd.targetX);
        const goalY = Math.floor(cmd.targetY);
        const path = hierarchicalPath(
          context.terrain,
          context.regionGraph,
          startX,
          startY,
          goalX,
          goalY,
          computeBridgeCells(state, context.terrain.width),
          WORLD_BRIDGE_MOVE_MULTIPLIER,
        );
        if (!path.found) break;
        const smoothed = path.smoothedCells;
        const waypoints = smoothed.length > 1 ? smoothed.slice(1) : smoothed.slice();
        spy.plannedPath = waypoints;
        spy.movementGoal = { x: goalX, y: goalY };
        break;
      }

      // v0M5.4 (2026-07-25) — Enter a Settlement. Requires Chebyshev
      // adjacency (≤ 1 cell from the settlement's cell). Auto-starts
      // Counter-Espionage if the settlement is friendly-owned.
      case 'enter_settlement_spy': {
        const spy = state.spies?.get(cmd.spyId);
        if (!spy) break;
        if (spy.ownerFactionId !== cmd.playerFactionId) break;
        if (spy.insideSettlementId) break; // Already inside somewhere.
        const settlement = state.settlements.get(cmd.settlementId);
        if (!settlement) break;
        // Adjacency check: Chebyshev distance ≤ 1 to the settlement's cell.
        const dx = Math.abs(spy.position.x - (settlement.cell.x + 0.5));
        const dy = Math.abs(spy.position.y - (settlement.cell.y + 0.5));
        if (Math.max(dx, dy) > 1.5) break;
        // Clear any pending movement, transition into the settlement,
        // reset infiltration counter. CE flag defaults to true only
        // when the settlement is friendly-owned.
        spy.plannedPath = null;
        spy.movementGoal = null;
        spy.insideSettlementId = cmd.settlementId;
        spy.infiltrationDays = 0;
        const province = state.provinces.get(settlement.provinceId);
        const isFriendly = province?.ownerFactionId === cmd.playerFactionId;
        spy.counterEspionage = isFriendly;
        // MFix11.2+.3 — successful direct enter also clears any pending
        // escort intent (the intent is now satisfied).
        spy.enterOnArrivalSettlementId = null;
        spy.enterOnArrivalOwnerAtIntent = null;
        break;
      }

      // MFix11.2+.3 (2026-07-27) — Escort-and-enter. Player RMB'd a
      // settlement while a spy was selected. Pathfind toward the
      // settlement's closest adjacent cell + record the intent so the
      // movement system auto-fires the enter transition when the spy
      // arrives within Chebyshev 1.5 of the settlement cell. Ownership
      // is captured at intent time so the movement system can invalidate
      // the intent if the province flips hands en route.
      case 'set_spy_enter_target': {
        const spy = state.spies?.get(cmd.spyId);
        if (!spy) break;
        if (spy.ownerFactionId !== cmd.playerFactionId) break;
        if (spy.insideSettlementId) break; // Already inside — use exit first.
        const settlement = state.settlements.get(cmd.settlementId);
        if (!settlement) break;
        const province = state.provinces.get(settlement.provinceId);
        const ownerAtIntent = province?.ownerFactionId ?? null;
        // Already within enter-radius? Skip pathfind — auto-enter fires
        // next tick regardless of path state.
        const dxNow = Math.abs(spy.position.x - (settlement.cell.x + 0.5));
        const dyNow = Math.abs(spy.position.y - (settlement.cell.y + 0.5));
        if (Math.max(dxNow, dyNow) <= 1.5) {
          spy.plannedPath = null;
          spy.movementGoal = null;
          spy.enterOnArrivalSettlementId = cmd.settlementId;
          spy.enterOnArrivalOwnerAtIntent = ownerAtIntent;
          break;
        }
        // Pick the settlement-adjacent cell closest to the spy's current
        // position (Euclidean), then pathfind there. Canonical NE-to-NW
        // tiebreak order for determinism.
        const bridgeCells = computeBridgeCells(state, context.terrain.width);
        const { width, height } = context.terrain;
        const sxCell = Math.round(spy.position.x);
        const syCell = Math.round(spy.position.y);
        const cx = settlement.cell.x;
        const cy = settlement.cell.y;
        const NEIGHBOUR_ORDER: readonly (readonly [number, number])[] = [
          [0, -1], [1, -1], [1, 0], [1, 1],
          [0, 1], [-1, 1], [-1, 0], [-1, -1],
        ];
        // Rank candidates by Euclidean distance from spy start (ties broken
        // by canonical order via stable insertion index).
        const candidates: { x: number; y: number; distSq: number; ord: number }[] = [];
        for (let i = 0; i < NEIGHBOUR_ORDER.length; i++) {
          const [ox, oy] = NEIGHBOUR_ORDER[i]!;
          const nx = cx + ox;
          const ny = cy + oy;
          if (nx < 0 || ny < 0 || nx >= width || ny >= height) continue;
          const ddx = nx - sxCell;
          const ddy = ny - syCell;
          candidates.push({ x: nx, y: ny, distSq: ddx * ddx + ddy * ddy, ord: i });
        }
        candidates.sort((a, b) => a.distSq - b.distSq || a.ord - b.ord);
        let planSet = false;
        for (const c of candidates) {
          const path = hierarchicalPath(
            context.terrain,
            context.regionGraph,
            sxCell,
            syCell,
            c.x,
            c.y,
            bridgeCells,
            WORLD_BRIDGE_MOVE_MULTIPLIER,
          );
          if (!path.found) continue;
          const smoothed = path.smoothedCells;
          // Path is [start, ..., goal] — drop the start cell so waypoints
          // begin at the next step. If smoothing collapses to just the
          // start (degenerate but possible for very short paths), fall
          // through to the direct-waypoint fallback below.
          const waypoints = smoothed.length > 1
            ? (smoothed.slice(1) as readonly (readonly [number, number])[])
            : null;
          if (waypoints === null || waypoints.length === 0) continue;
          spy.plannedPath = waypoints;
          spy.movementGoal = { x: c.x, y: c.y };
          spy.enterOnArrivalSettlementId = cmd.settlementId;
          spy.enterOnArrivalOwnerAtIntent = ownerAtIntent;
          planSet = true;
          break;
        }
        if (!planSet) {
          // Fallback — no hierarchical path found (either the coarse
          // corridor failed or every neighbour returned a degenerate
          // smoothing). Record the intent anyway with a direct 1-waypoint
          // plan toward the closest adjacent cell. The spy walks in a
          // straight line; obstacles are rare on open plains, and if the
          // player wants smarter routing they can dispatch a manual
          // move_spy first. Intent-only (no plan) is the last resort
          // when even the closest neighbour is off-map.
          const best = candidates[0];
          if (best !== undefined) {
            spy.plannedPath = [[best.x, best.y]];
            spy.movementGoal = { x: best.x, y: best.y };
          }
          spy.enterOnArrivalSettlementId = cmd.settlementId;
          spy.enterOnArrivalOwnerAtIntent = ownerAtIntent;
        }
        break;
      }

      // v0M5.4 — Exit the current Settlement. Re-materialize at the
      // settlement's cell + clear infiltration + CE state.
      // v0M5.5 (2026-07-25) — also clears Steal + Sabotage orders per
      // ESPIONAGE.md §2.4: presence is required to hold any order.
      case 'exit_settlement_spy': {
        const spy = state.spies?.get(cmd.spyId);
        if (!spy) break;
        if (spy.ownerFactionId !== cmd.playerFactionId) break;
        if (!spy.insideSettlementId) break;
        const settlement = state.settlements.get(spy.insideSettlementId);
        if (settlement) {
          spy.position = { x: settlement.cell.x + 0.5, y: settlement.cell.y + 0.5 };
        }
        spy.insideSettlementId = null;
        spy.infiltrationDays = 0;
        spy.counterEspionage = false;
        spy.stealActive = false;
        spy.stealActiveDays = 0;
        spy.sabotageTargetStructureIndex = null;
        spy.sabotageDaysRemaining = null;
        break;
      }

      // v0M5.4 — Toggle Counter-Espionage flag. Only legal inside a
      // friendly-owned settlement.
      case 'set_spy_counter_espionage': {
        const spy = state.spies?.get(cmd.spyId);
        if (!spy) break;
        if (spy.ownerFactionId !== cmd.playerFactionId) break;
        if (!spy.insideSettlementId) break;
        const settlement = state.settlements.get(spy.insideSettlementId);
        const province = settlement ? state.provinces.get(settlement.provinceId) : undefined;
        if (province?.ownerFactionId !== cmd.playerFactionId) break;
        spy.counterEspionage = cmd.value;
        break;
      }

      // v0M5.5 (2026-07-25) — Toggle Steal order. Requires:
      //   1. Spy inside an enemy-owned (or neutral) settlement.
      //   2. Owning faction has researched `Foreign Accounts`.
      // Toggling on clears any in-flight Sabotage (mutual exclusion).
      case 'set_spy_steal': {
        const spy = state.spies?.get(cmd.spyId);
        if (!spy) break;
        if (spy.ownerFactionId !== cmd.playerFactionId) break;
        if (!spy.insideSettlementId) break;
        const settlement = state.settlements.get(spy.insideSettlementId);
        const province = settlement ? state.provinces.get(settlement.provinceId) : undefined;
        if (!province) break;
        // Enemy or neutral only — not own province.
        if (province.ownerFactionId === cmd.playerFactionId) break;
        const faction = state.factions.get(cmd.playerFactionId);
        if (!faction) break;
        if (cmd.value) {
          const researched = faction.researchedKnowledge ?? [];
          if (!researched.includes('Foreign Accounts')) break;
          // Clear Sabotage on Steal toggle-on.
          spy.sabotageTargetStructureIndex = null;
          spy.sabotageDaysRemaining = null;
          spy.stealActive = true;
          spy.stealActiveDays = 0;
        } else {
          spy.stealActive = false;
          spy.stealActiveDays = 0;
        }
        break;
      }

      // v0M5.5 (2026-07-25) — Begin Sabotage against a completed
      // structure. Requires:
      //   1. Spy inside an enemy-owned settlement.
      //   2. Owning faction has researched `Insurgency Techniques`.
      //   3. structureIndex points to a valid completed structure in
      //      the hosting settlement's province.
      // Clears any active Steal (mutual exclusion) + sets the
      // countdown to `sabotage_completion_days`.
      case 'sabotage_structure_spy': {
        const spy = state.spies?.get(cmd.spyId);
        if (!spy) break;
        if (spy.ownerFactionId !== cmd.playerFactionId) break;
        if (!spy.insideSettlementId) break;
        const settlement = state.settlements.get(spy.insideSettlementId);
        const province = settlement ? state.provinces.get(settlement.provinceId) : undefined;
        if (!province) break;
        if (province.ownerFactionId === cmd.playerFactionId) break;
        const faction = state.factions.get(cmd.playerFactionId);
        if (!faction) break;
        const researched = faction.researchedKnowledge ?? [];
        if (!researched.includes('Insurgency Techniques')) break;
        if (cmd.structureIndex < 0 || cmd.structureIndex >= province.structures.length) break;
        // Clear Steal, set Sabotage countdown.
        spy.stealActive = false;
        spy.stealActiveDays = 0;
        spy.sabotageTargetStructureIndex = cmd.structureIndex;
        spy.sabotageDaysRemaining = SABOTAGE_COMPLETION_DAYS;
        break;
      }

      // v0M5.5 (2026-07-25) — Cancel in-flight Sabotage without exiting.
      case 'cancel_spy_sabotage': {
        const spy = state.spies?.get(cmd.spyId);
        if (!spy) break;
        if (spy.ownerFactionId !== cmd.playerFactionId) break;
        spy.sabotageTargetStructureIndex = null;
        spy.sabotageDaysRemaining = null;
        break;
      }

      // v0M7.2 (2026-07-25) — Issue an Engineer build order to an Army.
      // Gates per MAP.md §9.1:
      //   - Army is player-owned + has Engineer attachment.
      //   - Owning faction has researched the corresponding X Construction
      //     knowledge.
      //   - Target cell exists on the map + is passable + not already
      //     occupied by a WorldStructure or Settlement.
      // On success: replaces any prior build order with a fresh one at
      // progressDays=0. The build system (v0M7.3) accrues progress
      // when the Army parks on the cell.
      case 'set_army_build_order': {
        const army = state.armies.get(cmd.armyId);
        if (!army) break;
        if (army.ownerFactionId !== cmd.playerFactionId) break;
        const attachments = army.attachments ?? [];
        if (!attachments.includes('Engineer')) break;
        const faction = state.factions.get(cmd.playerFactionId);
        if (!faction) break;
        const researched = faction.researchedKnowledge ?? [];
        const knowledgeName = KNOWLEDGE_BY_KIND[cmd.kind];
        if (!researched.includes(knowledgeName)) break;

        const cx = Math.floor(cmd.targetCell.x);
        const cy = Math.floor(cmd.targetCell.y);
        const { width, height } = context.terrain;
        if (cx < 0 || cy < 0 || cx >= width || cy >= height) break;
        // Passable — use the strategic-map terrain-cost table (rejects
        // Mountain / Lake / Sea).
        const terrainId = context.terrain.terrain[cy * width + cx];
        if (terrainId === undefined) break;
        if (!Number.isFinite(terrainCost(terrainId))) break;
        // Cell must not already carry a WorldStructure OR Settlement
        // (one per cell — Bridge could theoretically overlap Road later,
        // but v0M7 keeps it strict; revisit if playtest asks for it).
        let cellOccupied = false;
        if (state.worldStructures) {
          for (const ws of state.worldStructures.values()) {
            if (ws.cell.x === cx && ws.cell.y === cy) { cellOccupied = true; break; }
          }
        }
        if (!cellOccupied) {
          for (const settlement of state.settlements.values()) {
            if (settlement.cell.x === cx && settlement.cell.y === cy) { cellOccupied = true; break; }
          }
        }
        if (cellOccupied) break;
        // Bridge-specific validation is a follow-up when strategic-map
        // river cells land (§9.5 open item). v0M7 accepts any passable
        // land cell for Bridge; effect wiring at v0M7.4c will no-op
        // until strategic rivers exist.
        //
        // v0M7.5b (2026-07-26) — Road polyline. If cmd.roadPath is
        // non-empty and kind==='road', validate every cell in it (in
        // bounds + passable + not already occupied by a WorldStructure
        // or Settlement) and store as army.buildOrder.roadPath. Empty
        // / missing = single-cell placement (same as pre-v0M7.5b).
        let validatedRoadPath: readonly { readonly x: number; readonly y: number }[] | undefined;
        if (cmd.kind === 'road' && cmd.roadPath && cmd.roadPath.length > 0) {
          const cells: { x: number; y: number }[] = [];
          let allValid = true;
          for (const raw of cmd.roadPath) {
            const rx = Math.floor(raw.x);
            const ry = Math.floor(raw.y);
            if (rx < 0 || ry < 0 || rx >= width || ry >= height) { allValid = false; break; }
            const terrainId = context.terrain.terrain[ry * width + rx];
            if (terrainId === undefined || !Number.isFinite(terrainCost(terrainId))) { allValid = false; break; }
            let cellOcc = false;
            if (state.worldStructures) {
              for (const ws of state.worldStructures.values()) {
                if (ws.cell.x === rx && ws.cell.y === ry) { cellOcc = true; break; }
              }
            }
            if (!cellOcc) {
              for (const settlement of state.settlements.values()) {
                if (settlement.cell.x === rx && settlement.cell.y === ry) { cellOcc = true; break; }
              }
            }
            // Same cell as targetCell is a duplicate — reject.
            if (rx === cx && ry === cy) { allValid = false; break; }
            if (cellOcc) { allValid = false; break; }
            cells.push({ x: rx, y: ry });
          }
          if (!allValid) break;
          validatedRoadPath = cells;
        }
        army.buildOrder = {
          kind: cmd.kind,
          targetCell: { x: cx, y: cy },
          progressDays: 0,
          ...(validatedRoadPath ? { roadPath: validatedRoadPath } : {}),
        };
        break;
      }

      // v0M7.2 — Cancel an in-flight build order.
      case 'cancel_army_build_order': {
        const army = state.armies.get(cmd.armyId);
        if (!army) break;
        if (army.ownerFactionId !== cmd.playerFactionId) break;
        army.buildOrder = null;
        break;
      }

      // v0M7.2 — Attacker toggles capture mode on an in-flight capture
      // attempt (Watch Tower at v1). Auto-capture is started by the
      // build/capture system when a hostile Army parks; this command
      // switches the default 'capture' mode to 'destroy' (or back).
      case 'set_world_structure_capture_mode': {
        const army = state.armies.get(cmd.armyId);
        if (!army) break;
        if (army.ownerFactionId !== cmd.playerFactionId) break;
        const ws = state.worldStructures?.get(cmd.worldStructureId);
        if (!ws) break;
        if (ws.ownerFactionId === cmd.playerFactionId) break; // Own tower — nothing to capture.
        // Army must be on the structure's cell.
        const ax = Math.floor(army.position.x);
        const ay = Math.floor(army.position.y);
        if (ax !== ws.cell.x || ay !== ws.cell.y) break;
        // Capture attempt must exist AND be attributed to this Army's faction.
        if (!ws.captureProgress) break;
        if (ws.captureProgress.byFactionId !== cmd.playerFactionId) break;
        ws.captureProgress.mode = cmd.mode;
        break;
      }
    }
  }
  return state;
}

/**
 * v0M7.2 (2026-07-25) — Map from WorldStructureKind → Knowledge row name
 * in KnowledgeTree.xlsx. Consulted by `set_army_build_order` to gate the
 * order against the owning faction's researched knowledge. Keep in sync
 * with the four sibling knowledge rows on the Engineering branch
 * (`Road Construction` / `Watch Tower Construction` / `Bridge Construction`
 * / `Fort Construction` — Fort row is Michael-owed at v0M7-design close).
 */
const KNOWLEDGE_BY_KIND: Readonly<Record<import('./state').WorldStructureKind, string>> = {
  road: 'Road Construction',
  watchtower: 'Watch Tower Construction',
  bridge: 'Bridge Construction',
  fort: 'Fort Construction',
};

/**
 * True iff `army.position` falls inside the cell set of the given
 * `provinceId` per `context.provinceMap` (v0M3.12). Skipped-as-true
 * when `context.provinceMap` is absent (test-fixture escape hatch) —
 * production scenario boot always wires the map, so the check is
 * always active in real play.
 */
function armyInProvince(
  context: SimContext,
  army: import('./state').Army,
  provinceId: import('./state').ProvinceId,
): boolean {
  const pmap = context.provinceMap;
  if (!pmap) return true; // Test-fixture escape hatch.
  const w = context.terrain.width;
  const h = context.terrain.height;
  const cx = Math.floor(army.position.x);
  const cy = Math.floor(army.position.y);
  if (cx < 0 || cx >= w || cy < 0 || cy >= h) return false;
  const mask = pmap[cy * w + cx];
  if (mask === undefined) return false;
  return (mask as unknown as number) === (provinceId as unknown as number);
}
